// Events.js
import React, { useContext, useEffect, useState } from 'react';
import { EventContext } from '../context/EventContext';
import EventCard from '../components/EventCard';
import { Card } from '../components/ui/Card';

const Events = () => {
  const { events, loading, error, fetchEvents } = useContext(EventContext);
  const [filter, setFilter] = useState('all'); // all, upcoming, past
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('dateDesc'); // dateDesc, dateAsc, titleAsc, titleDesc

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterEvents = () => {
    let filtered = events;

    // Filter by status
    if (filter === 'upcoming') {
      filtered = filtered.filter(event => {
        const eventDate = new Date(event.date);
        return eventDate >= new Date();
      });
    } else if (filter === 'past') {
      filtered = filtered.filter(event => {
        const eventDate = new Date(event.date);
        return eventDate < new Date();
      });
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(event =>
        event.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort events
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'dateDesc': // Latest first (default)
          return new Date(b.date) - new Date(a.date);
        case 'dateAsc': // Oldest first
          return new Date(a.date) - new Date(b.date);
        case 'titleAsc': // A-Z
          return (a.title || '').localeCompare(b.title || '');
        case 'titleDesc': // Z-A
          return (b.title || '').localeCompare(a.title || '');
        default:
          return new Date(b.date) - new Date(a.date);
      }
    });

    return filtered;
  };

  const filteredEvents = filterEvents();

  if (loading) {
    return (
      <div className="page events-page">
        <div className="loading">Loading events...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page events-page">
        <Card>
          <div className="error-message">
            <h2>Error</h2>
            <p>{error}</p>
            <button onClick={fetchEvents}>Retry</button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="page events-page">
      <div className="events-header">
        <h1>Events</h1>
        <p className="subtitle">Discover and participate in upcoming events</p>
      </div>

      <div className="events-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="controls-row">
          <div className="filter-buttons">
            <button
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All Events
            </button>
            <button
              className={`filter-btn ${filter === 'upcoming' ? 'active' : ''}`}
              onClick={() => setFilter('upcoming')}
            >
              Upcoming
            </button>
            <button
              className={`filter-btn ${filter === 'past' ? 'active' : ''}`}
              onClick={() => setFilter('past')}
            >
              Past
            </button>
          </div>

          <div className="sort-dropdown">
            <label htmlFor="sort-select">Sort by:</label>
            <select
              id="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="dateDesc">Latest First</option>
              <option value="dateAsc">Oldest First</option>
              <option value="titleAsc">Title (A-Z)</option>
              <option value="titleDesc">Title (Z-A)</option>
            </select>
          </div>
        </div>
      </div>

      {filteredEvents.length === 0 ? (
        <Card>
          <div className="no-events">
            <h3>No events found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Check back later for upcoming events'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="events-list">
          {filteredEvents.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
      )}

      <style jsx>{`
        .events-page {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem;
        }

        .events-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .events-header h1 {
          margin: 0;
          color: #1a1a1a;
          font-size: 2.5rem;
        }

        .subtitle {
          color: #666;
          margin-top: 0.5rem;
        }

        .events-controls {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .search-box {
          width: 100%;
        }

        .search-input {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 1rem;
          transition: border-color 0.2s;
        }

        .search-input:focus {
          outline: none;
          border-color: #2196f3;
        }

        .controls-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .filter-buttons {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .sort-dropdown {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .sort-dropdown label {
          font-size: 0.9rem;
          color: #666;
          white-space: nowrap;
        }

        .sort-select {
          padding: 0.5rem 1rem;
          border: 1px solid #ddd;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          font-size: 0.9rem;
          transition: border-color 0.2s;
        }

        .sort-select:focus {
          outline: none;
          border-color: #2196f3;
        }

        .filter-btn {
          padding: 0.5rem 1rem;
          border: 1px solid #ddd;
          background: white;
          border-radius: 20px;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.9rem;
        }

        .filter-btn:hover {
          background: #f5f5f5;
        }

        .filter-btn.active {
          background: #2196f3;
          color: white;
          border-color: #2196f3;
        }

        .events-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .no-events {
          text-align: center;
          padding: 3rem 2rem;
        }

        .no-events h3 {
          color: #666;
          margin: 0 0 0.5rem 0;
        }

        .no-events p {
          color: #999;
          margin: 0;
        }

        .error-message {
          text-align: center;
          padding: 2rem;
        }

        .error-message h2 {
          color: #d32f2f;
        }

        .error-message button {
          margin-top: 1rem;
          padding: 0.5rem 1.5rem;
          background: #2196f3;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .loading {
          text-align: center;
          padding: 3rem;
          font-size: 1.2rem;
          color: #666;
        }

        @media (max-width: 768px) {
          .events-page {
            padding: 1rem;
          }

          .events-header h1 {
            font-size: 2rem;
          }

          .events-list {
            grid-template-columns: 1fr;
          }

          .controls-row {
            flex-direction: column;
            align-items: stretch;
          }

          .filter-buttons {
            justify-content: center;
          }

          .sort-dropdown {
            justify-content: space-between;
          }
        }
      `}</style>
    </div>
  );
};

export default Events;
